import React, { createContext, useContext, useState, useEffect } from 'react';

export type Theme = {
  mode: 'light' | 'dark';
  colors: {
    background: string;
    text: string;
    border: string;
    card: string;
    primary: string;
    primaryLight: string;
    primaryDark: string;
    secondary: string;
    secondaryLight: string;
    secondaryDark: string;
    error: string;
    success: string;
    warning: string;
    info: string;
    divider: string;
    disabled: string;
    placeholder: string;
    backdrop: string;
    notification: string;
    toggleInactive: string;
    toggleThumb: string;
    filterBar: string;
    categoryUnselected: string;
    categoryUnselectedText: string;
  };
};

const defaultTheme: Theme = {
  mode: 'light',
  colors: {
    background: '#ffffff',
    text: '#2e2c2c',
    border: '#e0e0e0',
    card: '#ffffff',
    primary: '#00e3ff',
    primaryLight: '#00cce6',
    primaryDark: '#0099b3',
    secondary: '#757575',
    secondaryLight: '#9e9e9e',
    secondaryDark: '#616161',
    error: '#ff1744',
    success: '#00e676',
    warning: '#ffea00',
    info: '#00e5ff',
    divider: '#e0e0e0',
    disabled: '#9e9e9e',
    placeholder: '#9e9e9e',
    backdrop: 'rgba(0, 0, 0, 0.3)',
    notification: '#ffffff',
    toggleInactive: '#e0e0e0',
    toggleThumb: '#fff',
    filterBar: '#222',
    categoryUnselected: '#f0f0f0',
    categoryUnselectedText: '#888888',
  },
};

const darkTheme: Theme = {
  mode: 'dark',
  colors: {
    background: '#121212',
    text: '#ffffff',
    border: '#333333',
    card: '#1e1e1e',
    primary: '#00e3ff',
    primaryLight: '#33e9ff',
    primaryDark: '#00b3cc',
    secondary: '#666666',
    secondaryLight: '#888888',
    secondaryDark: '#444444',
    error: '#ff1744',
    success: '#00c853',
    warning: '#ffd600',
    info: '#00b0ff',
    divider: '#333333',
    disabled: '#666666',
    placeholder: '#666666',
    backdrop: 'rgba(0, 0, 0, 0.5)',
    notification: '#1e1e1e',
    toggleInactive: '#333333',
    toggleThumb: '#222',
    filterBar: '#222',
    categoryUnselected: '#232323',
    categoryUnselectedText: '#bbbbbb',
  },
};

const THEME_KEY = 'PIXANO_THEME_MODE';

type ThemeContextType = {
  theme: Theme;
  setTheme: (mode: 'light' | 'dark') => void;
  colors: Theme['colors'];
};

const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  setTheme: () => {},
  colors: defaultTheme.colors,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  useEffect(() => {
    const savedMode = localStorage.getItem(THEME_KEY);
    if (savedMode === 'dark') {
      setThemeState(darkTheme);
    } else {
      setThemeState(defaultTheme);
    }
  }, []);

  const setTheme = (mode: 'light' | 'dark') => {
    if (mode === 'dark') {
      setThemeState(darkTheme);
      localStorage.setItem(THEME_KEY, 'dark');
    } else {
      setThemeState(defaultTheme);
      localStorage.setItem(THEME_KEY, 'light');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, colors: theme.colors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeProvider;



